import { useRef, useLayoutEffect } from 'react';
import * as d3 from 'd3';
import styles from './StudyTimeLineChart.module.scss';

export interface StudyTimeDailyPoint {
  date: string;
  seconds: number;
}

/**
 * Día local YYYY-MM-DD (API). Evita ambigüedades de ISO/TZ y fallos de d3.timeParse en Safari.
 */
function parseCalendarDayString(raw: string): Date | null {
  const s = String(raw).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

function formatXTick(date: Date, locale: string, xMin: Date, xMax: Date): string {
  const spanDays = Math.max(
    1,
    Math.round((xMax.getTime() - xMin.getTime()) / 86400000)
  );
  const sameYear = xMin.getFullYear() === xMax.getFullYear();
  const showYear = !sameYear || spanDays > 120;
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    ...(showYear ? { year: 'numeric' as const } : {}),
  });
}

function formatYTick(seconds: number): string {
  if (seconds >= 3600) {
    const h = seconds / 3600;
    return h >= 10 ? `${Math.round(h)}h` : `${h.toFixed(1)}h`;
  }
  if (seconds >= 60) {
    return `${Math.round(seconds / 60)}m`;
  }
  return `${Math.round(seconds)}s`;
}

export interface StudyTimeLineChartProps {
  data: StudyTimeDailyPoint[];
  locale: string;
}

/**
 * Gráfico de líneas (D3) — eje X días, eje Y tiempo de estudio (segundos).
 */
export function StudyTimeLineChart({ data, locale }: StudyTimeLineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    const svgEl = svgRef.current;
    if (!el || !svgEl || data.length === 0) return;

    const draw = () => {
      const width = el.clientWidth || 640;
      const height = 340;
      /** Margen izquierdo más compacto que antes (56); etiquetas Y siguen a la izquierda del gráfico. */
      const margin = { top: 20, right: 16, bottom: 52, left: 38 };
      const innerW = Math.max(0, width - margin.left - margin.right);
      const innerH = height - margin.top - margin.bottom;

      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();
      svg.attr('width', width).attr('height', height).attr('role', 'img');

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const points = data
        .map((d) => {
          const t = parseCalendarDayString(d.date);
          return t ? { ...d, t } : null;
        })
        .filter((p): p is StudyTimeDailyPoint & { t: Date } => p !== null)
        .sort((a, b) => a.t.getTime() - b.t.getTime());

      if (points.length === 0) return;

      const xMin = d3.min(points, (d) => d.t)!;
      const xMax = d3.max(points, (d) => d.t)!;
      const xDomain: [Date, Date] =
        points.length === 1
          ? [d3.timeDay.offset(xMin, -1), d3.timeDay.offset(xMax, 1)]
          : [xMin, xMax];
      const x = d3.scaleTime().domain(xDomain).range([0, innerW]);

      const maxY = Math.max(d3.max(points, (d) => d.seconds) ?? 0, 60);
      const y = d3.scaleLinear().domain([0, maxY]).nice().range([innerH, 0]);

      const lineGen = d3
        .line<(typeof points)[number]>()
        .x((d) => x(d.t))
        .y((d) => y(d.seconds))
        .curve(d3.curveMonotoneX);

      const tickCount = Math.min(10, Math.max(4, Math.floor(innerW / 72)));

      const xAxis = d3
        .axisBottom(x)
        .ticks(tickCount)
        .tickFormat((d) => formatXTick(d as Date, locale, xMin, xMax));

      const yAxis = d3.axisLeft(y).ticks(6).tickFormat((v) => formatYTick(Number(v)));

      const linePath = lineGen(points) ?? '';

      g.append('path')
        .datum(points)
        .attr('class', styles.chart__line ?? '')
        .attr('d', linePath);

      g.append('g')
        .attr('class', styles.chart__axis ?? '')
        .attr('transform', `translate(0,${innerH})`)
        .call(xAxis)
        .selectAll('text')
        .attr('transform', 'rotate(-35)')
        .style('text-anchor', 'end');

      g.append('g').attr('class', styles.chart__axis ?? '').call(yAxis);

      g.selectAll('circle.pt')
        .data(points)
        .join('circle')
        .attr('class', styles.chart__dot ?? '')
        .attr('cx', (d) => x(d.t))
        .attr('cy', (d) => y(d.seconds))
        .attr('r', 3.5)
        .append('title')
        .text((d) => `${d.date}: ${formatYTick(d.seconds)}`);
    };

    draw();

    const ro = new ResizeObserver(() => draw());
    ro.observe(el);
    return () => ro.disconnect();
  }, [data, locale]);

  return (
    <div ref={containerRef} className={styles.chart}>
      <svg ref={svgRef} className={styles.chart__svg} />
    </div>
  );
}
